<template>
    <div class="flex flex-row h-screen bg-gray-100">
        <!-- Left Sidebar - Contacts -->
        <div class="w-1/3 max-w-sm bg-white shadow-lg overflow-hidden flex flex-col">
            <!-- Header -->
            <div class="bg-gradient-to-r from-blue-600 to-indigo-800 px-6 py-4">
                <h1 class="text-3xl font-bold text-white">Chats</h1>
                <!-- Search Bar -->
                <div class="mt-4 relative">
                    <input type="text"
                        class="w-full pl-10 pr-4 py-2 rounded-lg bg-white bg-opacity-20 text-white placeholder-gray-200 focus:outline-none focus:ring-2 focus:ring-white"
                        placeholder="Search Employees" v-model="searchData.name" @keyup="getEmployeesListChat">
                    <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20"
                            xmlns="http://www.w3.org/2000/svg">
                            <path fill-rule="evenodd"
                                d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                clip-rule="evenodd"></path>
                        </svg>
                    </div>
                </div>

                <!-- Filter Buttons -->
                <div class="flex mt-4 space-x-2">
                    <router-link :to="'/' + currentUrl + '/one-to-one-chat'">
                        <button
                            class="px-6 py-2 rounded-lg bg-white text-indigo-800 font-medium shadow-md hover:bg-gray-100 transition duration-200">
                            Inbox
                        </button>
                    </router-link>
                    <router-link :to="'/' + currentUrl + '/group-chat'">
                        <button
                            class="px-6 py-2 rounded-lg bg-indigo-900 bg-opacity-40 text-white font-medium hover:bg-opacity-60 transition duration-200">
                            Groups
                        </button>
                    </router-link>
                </div>
            </div>

            <!-- Contact List -->
            <div class="flex-1 overflow-y-auto px-4 py-2">
                <ul class="space-y-1">
                    <li v-for="e in empList.data" v-bind:key="e.id" class="border-b border-gray-100 last:border-b-0">
                        <router-link :to="'/' + currentUrl + '/one-to-one-chat/' + e.id"
                            @click.native="readOneToOneMessage(e.id)"
                            class="block p-3 rounded-lg hover:bg-gray-50 transition duration-200">
                            <div class="flex items-center space-x-3">
                                <div class="relative flex-shrink-0">
                                    <img class="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                                        :src="imagePath + '/profile-images/' + e.profile_image || 'https://images.unsplash.com/photo-1590031905470-a1a1feacbb0b?ixlib=rb-1.2.1&amp;auto=format&amp;fit=facearea&amp;facepad=3&amp;w=144&amp;h=144'"
                                        alt="Profile" />
                                    <span
                                        class="absolute right-0 bottom-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"
                                        v-if="e.online"></span>
                                </div>

                                <div class="flex-1 min-w-0">
                                    <div class="flex justify-between items-center">
                                        <h3 class="text-sm font-semibold text-gray-900 truncate">
                                            {{ e.first_name }} {{ e.last_name }}
                                        </h3>
                                        <span class="text-xs text-gray-500">{{ e.last_created_at | fromNow }}</span>
                                    </div>
                                    <p class="text-sm text-gray-500 truncate">{{ e.last_content }}</p>
                                </div>

                                <div v-if="e.new_message.length" class="flex-shrink-0 ml-2">
                                    <span
                                        class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-indigo-600 text-xs font-medium text-white">
                                        {{ e.new_message.length }}
                                    </span>
                                </div>
                            </div>
                        </router-link>
                    </li>
                </ul>
            </div>
        </div>

        <!-- Right Side - Chat Area -->
        <div class="flex-1 flex flex-col bg-white shadow-lg rounded-l-xl overflow-hidden">
            <div v-if="this.$route.params.id" class="flex flex-col h-full">
                <!-- Chat Header -->
                <div class="px-6 py-4 border-b border-gray-200 flex items-center">
                    <div class="flex items-center">
                        <div class="relative">
                            <img :src="imagePath + '/profile-images/' + empChat.profile_image" alt=""
                                class="w-12 h-12 rounded-full object-cover border-2 border-gray-200">
                            <span
                                class="absolute right-0 bottom-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>
                        </div>
                        <div class="ml-4">
                            <h2 class="text-xl font-semibold text-gray-800">{{ empChat.first_name }} {{
                                empChat.last_name }}</h2>
                            <p class="text-sm text-gray-500">Online</p>
                        </div>
                    </div>

                    <div class="ml-auto">
                        <button
                            class="p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                                stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                            </svg>
                        </button>
                    </div>
                </div>

                <!-- Messages Area - KEY FIX: Added flex-1 and min-0 to ensure proper scrolling -->
                <div id="chat-gui" class="flex-1 min-h-0 overflow-y-auto p-6 bg-gray-50" v-chat-scroll>
                    <div class="flex justify-center mb-4" v-if="groupData.limit < total">
                        <button
                            class="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg font-medium hover:bg-indigo-200 transition duration-200 focus:outline-none"
                            @click="loadMoreMessages">
                            Load More Messages
                        </button>
                    </div>

                    <div class="space-y-4">
                        <div v-for="m in messagesList" v-bind:key="m.id">
                            <!-- Received Message -->
                            <div class="flex items-end space-x-2" v-if="authUser.emp_id != m.sender_id">
                                <img class="w-8 h-8 rounded-full flex-shrink-0"
                                    :src="imagePath + '/profile-images/' + m.profile_image" alt="Profile">

                                <div class="max-w-md">
                                    <div class="bg-white rounded-t-lg rounded-r-lg p-4 shadow-sm relative">
                                        <!-- Text Content -->
                                        <div v-if="m.message_type == 'TEXT' || m.message_type == 'COMBINED'"
                                            class="text-gray-700 text-sm mb-2" v-html="convertToHtml(m.content)"></div>

                                        <!-- File Attachment -->
                                        <div v-if="m.message_type == 'FILE' || m.message_type == 'COMBINED'"
                                            class="mt-2">
                                            <a href="javascript:void(0)"
                                                @click="downloadAttachment(m.id, m.file_content || m.content, 'one')"
                                                class="flex items-center space-x-2 text-indigo-600 hover:text-indigo-800">
                                                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none"
                                                    viewBox="0 0 24 24" stroke="currentColor">
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2"
                                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                                <span class="font-medium">Download Attachment</span>
                                            </a>
                                        </div>
                                    </div>

                                    <div class="mt-1 text-xs text-gray-500">{{ m.created_at | fromNow }}</div>
                                </div>
                            </div>

                            <!-- Sent Message -->
                            <div class="flex items-end justify-end space-x-2" v-else>
                                <div class="max-w-md">
                                    <div class="bg-indigo-600 rounded-t-lg rounded-l-lg p-4 shadow-sm relative">
                                        <!-- Text Content -->
                                        <div v-if="m.message_type == 'TEXT' || m.message_type == 'COMBINED'"
                                            class="text-white text-sm mb-2" v-html="convertToHtml(m.content)"></div>

                                        <!-- File Attachment -->
                                        <div v-if="m.message_type == 'FILE' || m.message_type == 'COMBINED'"
                                            class="mt-2">
                                            <a href="javascript:void(0)"
                                                @click="downloadAttachment(m.id, m.file_content || m.content, 'one')"
                                                class="flex items-center space-x-2 text-white hover:text-indigo-100">
                                                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none"
                                                    viewBox="0 0 24 24" stroke="currentColor">
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2"
                                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                                <span class="font-medium">Download Attachment</span>
                                            </a>
                                        </div>
                                    </div>

                                    <div class="mt-1 text-xs text-gray-500 text-right">{{ m.created_at | fromNow }}
                                    </div>
                                </div>

                                <img class="w-8 h-8 rounded-full flex-shrink-0"
                                    :src="imagePath + '/profile-images/' + m.profile_image" alt="Profile">
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Message Input - KEY FIX: Ensuring this is properly positioned at the bottom -->
                <div class="p-4 border-t border-gray-200 bg-white mt-auto">
                    <!-- Selected File Preview -->
                    <div v-if="selectedFile" class="mb-2 p-2 bg-indigo-50 rounded-lg flex items-center justify-between">
                        <div class="flex items-center space-x-2">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-indigo-600" fill="none"
                                viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span class="text-sm text-indigo-800 truncate max-w-xs">{{ selectedFileName }}</span>
                        </div>
                        <button @click="removeSelectedFile"
                            class="p-1 rounded-full bg-indigo-100 text-indigo-700 hover:bg-indigo-200">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                                stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <div class="flex items-end space-x-2">
                        <div class="flex-1 relative">
                            <textarea type="text" placeholder="Type your message..."
                                class="w-full px-4 py-3 rounded-lg bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                                rows="1" v-model="dataMsg.message" v-on:keyup.enter="sendCombinedMessage"></textarea>

                            <button type="button"
                                class="absolute bottom-3 right-3 text-gray-500 hover:text-indigo-600 focus:outline-none"
                                @click="$refs.file.click()">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                                    stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                </svg>
                            </button>
                            <input type="file" ref="file" class="hidden" @change="handleFileSelection">
                        </div>

                        <button type="button"
                            class="flex items-center justify-center p-3 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-md"
                            @click="sendCombinedMessage">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 transform rotate-90" fill="none"
                                viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Empty State -->
            <div v-else class="flex-1 flex flex-col items-center justify-center bg-gray-50">
                <div class="p-8 text-center">
                    <div class="inline-flex justify-center items-center w-24 h-24 rounded-full bg-indigo-100 mb-6">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-indigo-600" fill="none"
                            viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                    </div>
                    <h3 class="text-2xl font-bold text-gray-800 mb-2">No conversation selected</h3>
                    <p class="text-gray-500 max-w-md">Select a conversation from the list to start chatting or search
                        for a specific contact.</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
/* eslint-disable */
import AppMixin from '../../../mixins/AppMixin'
import Api from '../../../router/api'

export default {
    name: 'MessageMyTeam',
    mixins: [AppMixin],
    data() {
        return {
            dataMsg: {
                message: '',
                rId: ''
            },
            imageMsg: {
                file: '',
                type: 'one',
                rId: ''
            },
            currentUrl: '',
            selectedFile: null,
            selectedFileName: '',
        }
    },
    methods: {
        loadMoreMessages: function () {
            let that = this
            that.groupData.limit = that.groupData.limit + that.groupData.limit
            that.getOneToOneMessage()
        },

        handleFileSelection: function (e) {
            if (e.target.files.length > 0) {
                this.selectedFile = e.target.files[0]
                this.selectedFileName = this.selectedFile.name
                this.imageMsg.file = this.selectedFile
            }
        },

        removeSelectedFile: function () {
            this.selectedFile = null
            this.selectedFileName = ''
            this.imageMsg.file = ''
            // Reset file input
            this.$refs.file.value = ''
        },

        sendCombinedMessage: function () {
            let that = this

            // Get recipient ID
            const recipientId = this.$route.params.id
            that.dataMsg.rId = recipientId
            that.imageMsg.rId = recipientId

            // Check if we have both a message and an attachment
            const hasMessage = !!that.dataMsg.message.trim()
            const hasAttachment = !!that.selectedFile

            // Empty message and no attachment - show error
            if (!hasMessage && !hasAttachment) {
                this.$swal({
                    icon: 'error',
                    title: 'Error',
                    text: 'Please enter a message or attach a file',
                    showConfirmButton: true
                })
                return
            }

            // We have both a message and an attachment - send combined
            if (hasMessage && hasAttachment) {
                const formData = new FormData()
                formData.append('file', that.selectedFile)
                formData.append('message', that.dataMsg.message)
                formData.append('type', 'one')
                formData.append('rId', recipientId)

                let headers = {
                    'Content-Type': 'multipart/form-data',
                    'Access-Control-Allow-Origin': '*'
                }

                // Call API to send combined message
                Api.sendCombinedMessage(formData, headers).then(response => {
                    // Reset form
                    that.dataMsg.message = ''
                    that.removeSelectedFile()
                    that.getOneToOneMessage()
                    that.getEmployeesListChat()
                }).catch((error) => {
                    this.$swal({
                        icon: 'error',
                        title: 'Error',
                        text: error.response && error.response.data && error.response.data.message
                            ? error.response.data.message
                            : 'Failed to send message',
                        showConfirmButton: true
                    })
                })

                return
            }

            // Only message, no attachment
            if (hasMessage) {
                Api.sendOneToOneMessage(that.dataMsg).then(response => {
                    that.dataMsg.message = ''
                    that.getOneToOneMessage()
                    that.getEmployeesListChat()
                }).catch((error) => {
                    this.$swal({
                        icon: 'error',
                        title: 'Error',
                        text: error.response && error.response.data && error.response.data.message
                            ? error.response.data.message
                            : 'Failed to send message',
                        showConfirmButton: true
                    })
                })
                return
            }

            // Only attachment, no message
            if (hasAttachment) {
                const formData = new FormData()
                formData.append('file', that.selectedFile)
                formData.append('type', 'one')
                formData.append('rId', recipientId)

                let headers = {
                    'Content-Type': 'multipart/form-data',
                    'Access-Control-Allow-Origin': '*'
                }

                Api.sendAttachment(formData, headers, 'one').then(response => {
                    that.removeSelectedFile()
                    that.getOneToOneMessage()
                    that.getEmployeesListChat()
                }).catch((error) => {
                    this.$swal({
                        icon: 'error',
                        title: 'Error',
                        text: error.response && error.response.data && error.response.data.message
                            ? error.response.data.message
                            : 'Failed to send attachment',
                        showConfirmButton: true
                    })
                })
            }
        },

        scrollToBottom() {
            // Use nextTick to ensure DOM is updated
            this.$nextTick(() => {
                const chatContainer = document.getElementById('chat-gui');
                if (chatContainer) {
                    chatContainer.scrollTop = chatContainer.scrollHeight;
                }
            });
        },
    },
    watch: {
        // Watch for new messages and scroll to bottom
        messagesList: {
            handler() {
                this.scrollToBottom();
            },
            deep: true
        }
    },
    created() {
        var url = document.URL.split('/');
        this.currentUrl = url[3]

        this.getEmployeesListChat()
        this.getAuthUser()
        this.getEmployeeChat(this.$route.params.id)
        this.getOneToOneMessage()
        if (this.$route.name == 'OneToOneChat') {
            window.Echo.channel('chat' + this.user.id)
                .listen('MessageSent', (e) => {
                    console.log(e)
                    // this.getOneToOneMessage()
                    this.messagesList.push({
                        'id': e.messageSent.id,
                        'content': e.messageSent.content,
                        'message_type': e.messageSent.message_type,
                        'first_name': e.first_name,
                        'last_name': e.last_name,
                        'profile_image': e.profile_image,
                        'created_at': e.messageSent.created_at,
                        'file_content': e.messageSent.file_content
                    })
                });
        }
    },
    mounted() {
        // Initial scroll to bottom when component is mounted
        this.scrollToBottom();
    }
}
</script>

<style scoped>
/* Auto-resize textarea */
textarea {
    min-height: 44px;
    max-height: 120px;
    overflow-y: auto;
}

/* Message bubbles */
.bg-indigo-600 {
    position: relative;
}

.bg-white {
    position: relative;
}

/* Custom scrollbar */
::-webkit-scrollbar {
    width: 6px;
}

::-webkit-scrollbar-track {
    background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
    background: #c7d2fe;
    border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
    background: #818cf8;
}

/* Ensure proper chat layout */
#chat-gui {
    display: flex;
    flex-direction: column;
    height: calc(100% - 160px); /* Adjust based on header and input heights */
}
</style>


// WEBPACK FOOTER //
// src/components/common/chat/OneToOneChat.vue